'use client'

import { LineSwapHorizontal } from '@qr-front/icons/line-swap-horizontal'
import { LineSwapVertical } from '@qr-front/icons/line-swap-vertical'
import { ColorPicker } from '@qr-front/ui/components/ColorPicker'
import { cn } from '@qr-front/ui/utils'
import { useTranslations } from 'next-intl'
import { debounce } from 'radash'
import { useMemo, useRef } from 'react'

import { getColorPickerTranslations } from '../utils/getColorPickerTranslations'

type TColorFieldChange = {
	colorElement: 'primary' | 'secondary'
	colorValue: string
	inputMethod?: 'swap'
}

const useColorChangeReporter = (
	colorElement: TColorFieldChange['colorElement'],
	onColorChange?: (details: TColorFieldChange) => void,
) => {
	const onColorChangeRef = useRef(onColorChange)
	onColorChangeRef.current = onColorChange

	return useMemo(
		() =>
			debounce({ delay: 200 }, (colorValue: string) =>
				onColorChangeRef.current?.({ colorElement, colorValue }),
			),
		[colorElement],
	)
}

export const PrimaryAndSecondaryColorFields = ({
	primaryColor,
	setPrimaryColor,
	primaryLabel,
	isPrimaryDisabled,
	secondaryColor,
	setSecondaryColor,
	secondaryLabel,
	isSecondaryDisabled,
	onColorChange,
}: {
	primaryColor: string
	setPrimaryColor: (value: string) => void
	primaryLabel: string
	secondaryColor: string
	isPrimaryDisabled?: boolean
	setSecondaryColor: (value: string) => void
	secondaryLabel: string
	isSecondaryDisabled?: boolean
	onColorChange?: (details: TColorFieldChange) => void
}) => {
	const t = useTranslations()

	const reportPrimary = useColorChangeReporter('primary', onColorChange)
	const reportSecondary = useColorChangeReporter('secondary', onColorChange)

	const handlePrimaryChange = (value: string) => {
		setPrimaryColor(value)
		reportPrimary(value)
	}

	const handleSecondaryChange = (value: string) => {
		setSecondaryColor(value)
		reportSecondary(value)
	}

	const primaryColorField = (
		<ColorPicker
			value={primaryColor}
			onValueChange={handlePrimaryChange}
			setValue={handlePrimaryChange}
			label={primaryLabel}
			disabled={isPrimaryDisabled}
			transparentLabel={t('ui__generic__transparent')}
			translations={getColorPickerTranslations(t)}
		/>
	)

	const secondaryColorField = (
		<ColorPicker
			value={secondaryColor}
			onValueChange={handleSecondaryChange}
			setValue={handleSecondaryChange}
			label={secondaryLabel}
			disabled={isSecondaryDisabled}
			transparentLabel={t('ui__generic__transparent')}
			translations={getColorPickerTranslations(t)}
		/>
	)

	const handleClickSwap = () => {
		const primaryColorSnapshot = primaryColor
		setPrimaryColor(secondaryColor)
		setSecondaryColor(primaryColorSnapshot)
		onColorChange?.({
			colorElement: 'primary',
			colorValue: secondaryColor,
			inputMethod: 'swap',
		})
		onColorChange?.({
			colorElement: 'secondary',
			colorValue: primaryColorSnapshot,
			inputMethod: 'swap',
		})
	}

	const isOnlyOneColorEnabled = isPrimaryDisabled || isSecondaryDisabled

	const buttonSwap = (
		<button
			onClick={handleClickSwap}
			disabled={isOnlyOneColorEnabled}
			className={cn(
				'flex h-10 shrink-0 cursor-pointer items-center justify-center gap-2 self-center rounded-full bg-transparent px-4 text-grey transition tablet:mb-1 tablet:w-10 tablet:self-end tablet:px-0 hover:[&:not([disabled])]:text-black tablet:hover:[&:not([disabled])]:bg-white',
				isOnlyOneColorEnabled && 'cursor-not-allowed',
			)}
		>
			<span className="type-body-small tablet:hidden">
				{t('generator__content_form_section__design__swap_button')}
			</span>
			<LineSwapVertical className="tablet:hidden" />
			<LineSwapHorizontal className="hidden tablet:block" />
		</button>
	)

	return (
		<div className="flex flex-col gap-4 tablet:flex-row">
			{primaryColorField}
			{buttonSwap}
			{secondaryColorField}
		</div>
	)
}
